import { ButtonProps, SxProps } from "@outschool/backpack";
import { centsToDollars } from "@outschool/business-rules";
import {
  ActivityListing_ListingFragment,
  SaveButton_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  onboardingQuizPath,
  pathFromLocation,
} from "@outschool/routes";
import {
  IntegrationCategory,
  useAnalytics,
  useProductAddedToWishlist,
  useTrackEvent,
} from "@outschool/ui-analytics";
import {
  AuthTrigger,
  AuthTriggerActionTypes,
  useLoginFlowContext,
  useSession,
} from "@outschool/ui-auth";
import { HeartButton } from "@outschool/ui-components-shared";
import {
  TrackingProperties,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import React from "react";

import AddToCollectionModal from "./AddToCollectionModal";
import {
  saveActivityMutation,
  useSavedActivityMutations,
} from "./graphql/mutations";

import type { AddToCollectionModalProps } from "./AddToCollectionModal";

interface SaveButtonProps {
  activity: SaveButton_ActivityFragment;
  leader?: ActivityListing_ListingFragment["leader"];
  isSet?: boolean;
  onChange?: (_args?: any) => void;
  trackingProperties?: TrackingProperties;
  disabled?: boolean;
  // styling props
  simpleHeart?: boolean;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  sx?: SxProps;
}

export function SaveButton({
  activity,
  leader,
  isSet,
  onChange = (_saved?: boolean) => {},
  trackingProperties = {},
  disabled = false,
  simpleHeart,
  size,
  variant,
  sx,
}: SaveButtonProps) {
  const { enterLoginFlow } = useLoginFlowContext();
  const { isLoggedIn } = useSession();
  const analytics = useAnalytics();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const trackProductAddedToWishlist = useProductAddedToWishlist();
  const [isSaved, setIsSaved] = React.useState(isSet);
  const [addToCollectionModalMode, setAddToCollectionModalMode] =
    React.useState<AddToCollectionModalProps["mode"]>("closed");

  React.useEffect(() => {
    setIsSaved(isSet);
  }, [isSet, setIsSaved]);

  const {
    saveActivity: [saveActivity, savedActivity],
    unsaveActivity: [unsaveActivity, activityIsUnsaved],
  } = useSavedActivityMutations(activity?.uid);

  const saveHeartImpressionNodeRef = React.useRef<HTMLElement | null>(null);
  const { trackTouch: trackSaveHeartTouch } = useImpressionTracking({
    node: saveHeartImpressionNodeRef.current,
    trackingLabel: "save_heart",
    sharedProperties: {
      activity_uid: activity.uid,
      is_saved: isSaved,
      ...trackingProperties,
    },
    uniqueId: activity.uid,
  });

  React.useEffect(() => {
    if (savedActivity && savedActivity.activity_uid === activity.uid) {
      setIsSaved(true);
    }
  }, [savedActivity, activity?.uid]);

  React.useEffect(() => {
    if (activityIsUnsaved) {
      setIsSaved(false);
    }
  }, [activityIsUnsaved]);

  const handleSaveClick = async (saved: any) => {
    const referer = pathFromLocation(window.location);
    const attribution = await Promise.race([
      analytics.attribution(),
      new Promise(resolve => {
        setTimeout(() => resolve(undefined), 1000);
      }),
    ]);
    if (!isLoggedIn) {
      enterLoginFlow({
        authTrigger: AuthTrigger.SAVE_CLASS,
        postLoginAction: {
          actionType: AuthTriggerActionTypes.SAVE_AFTER_LOGIN,
          welcomeModalData: undefined,
          graphqlMutation: {
            mutation: saveActivityMutation,
            variables: {
              activityUid: activity.uid,
              referer,
              attribution,
            },
          },
          shouldPopUpWelcomeModal: false,
        },
        shouldNavigateOverwrite: false,
        newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
          savedActivityUid: activity.uid,
        }),
        newUrlAfterLogin: pathFromLocation(window.location),
      });
    } else {
      if (saved) {
        trackProductAddedToWishlist("class", "favorites", {
          // @ts-ignore TS(2322): Type 'SaveButton_ActivityFragment' is not assignab... Remove this comment to see the full error message
          activity,
          // @ts-ignore TS(2322): Type '{ [key: string]: unknown; name: string; } | ... Remove this comment to see the full error message
          leader,
        });
        trackAdEvent("Save Class", {
          products: [
            {
              product_id: activity.slug_id,
              name: activity.title,
              quantity: "1",
              // @ts-ignore TS(2345): Argument of type 'number | null' is not assignable... Remove this comment to see the full error message
              price: centsToDollars(activity.price_cents),
              class_image_url: activity.details.photo,
              class_id: activity.uid,
            },
          ],
        });
        await saveActivity(referer, attribution);
        setAddToCollectionModalMode("add");
      } else if (activity?.savedClassLists?.length > 0) {
        setAddToCollectionModalMode("edit");
      } else {
        await unsaveActivity();
      }
    }
    trackSaveHeartTouch({ is_saved: saved });
    onChange(saved);
  };

  const { t } = useTranslation("client\\components\\saving\\SaveButton");

  return (
    <>
      <AddToCollectionModal
        mode={addToCollectionModalMode}
        onDone={() => {
          /* eslint-disable-next-line i18next/no-literal-string */
          setAddToCollectionModalMode("closed");
        }}
        activity={activity}
      />
      <HeartButton
        isSet={!!isSaved}
        setLabel={t`Saved`}
        unsetLabel={t`Save`}
        onChange={disabled ? null : handleSaveClick}
        simpleHeart={simpleHeart}
        variant={variant}
        size={size}
        sx={sx}
        disabled={disabled}
        ref={saveHeartImpressionNodeRef as any}
      />
    </>
  );
}

/*
 * Alias for the simple heart version of the save button
 */
export function SaveHeart(props: SaveButtonProps) {
  return <SaveButton simpleHeart {...props} />;
}
